import React, { useRef, useCallback, useState } from 'react';
import { 
  FaBold, 
  FaItalic, 
  FaUnderline, 
  FaListUl, 
  FaListOl,
  FaQuoteLeft,
  FaLink,
  FaUnlink,
  FaUndo,
  FaRedo,
  FaImage,
  FaTimes
} from 'react-icons/fa';
import './RichTextEditor.css';

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const savedSelectionRef = useRef(null);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, []);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleHeading = useCallback((tag) => {
    document.execCommand('formatBlock', false, tag);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  const handleLink = useCallback(() => {
    const url = prompt('Vnesite URL:', 'https://');
    if (url) {
      document.execCommand('createLink', false, url);
      handleInput();
    }
  }, [handleInput]);

  // Save selection before opening modal
  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  }, []);

  // Restore selection after modal
  const restoreSelection = useCallback(() => {
    if (savedSelectionRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
    }
  }, []);

  const openImageModal = useCallback(() => {
    saveSelection();
    setImageUrl('');
    setImageAlt('');
    setShowImageModal(true);
  }, [saveSelection]);

  const insertImage = useCallback(() => {
    if (!imageUrl) return;
    
    editorRef.current?.focus();
    restoreSelection();
    
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = imageAlt || 'Slika';
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.borderRadius = '8px';
    img.style.margin = '16px 0';
    img.style.display = 'block';
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(img);
      
      // Move cursor after the image
      range.setStartAfter(img);
      range.setEndAfter(img);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current?.appendChild(img);
    }
    
    setShowImageModal(false);
    setImageUrl('');
    setImageAlt('');
    handleInput();
  }, [imageUrl, imageAlt, restoreSelection, handleInput]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  }, [handleInput]);

  // Set initial content
  React.useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, []);

  return (
    <div className="rich-editor-container">
      {/* Toolbar */}
      <div className="rich-editor-toolbar">
        <div className="toolbar-group">
          <select 
            className="toolbar-select"
            onChange={(e) => {
              if (e.target.value) {
                handleHeading(e.target.value);
                e.target.value = '';
              }
            }}
            defaultValue=""
          >
            <option value="" disabled>Naslov</option>
            <option value="h1">Naslov 1</option>
            <option value="h2">Naslov 2</option>
            <option value="h3">Naslov 3</option>
            <option value="h4">Naslov 4</option>
            <option value="p">Odstavek</option>
          </select>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('bold')}
            title="Krepko (Ctrl+B)"
          >
            <FaBold />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('italic')}
            title="Ležeče (Ctrl+I)"
          >
            <FaItalic />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('underline')}
            title="Podčrtano (Ctrl+U)"
          >
            <FaUnderline />
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertUnorderedList')}
            title="Točkovni seznam"
          >
            <FaListUl />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertOrderedList')}
            title="Oštevilčen seznam"
          >
            <FaListOl />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('formatBlock', 'blockquote')}
            title="Citat"
          >
            <FaQuoteLeft />
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={handleLink}
            title="Vstavi povezavo"
          >
            <FaLink />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('unlink')}
            title="Odstrani povezavo"
          >
            <FaUnlink />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={openImageModal}
            title="Vstavi sliko"
          >
            <FaImage />
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('undo')}
            title="Razveljavi (Ctrl+Z)"
          >
            <FaUndo />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('redo')}
            title="Ponovi (Ctrl+Y)"
          >
            <FaRedo />
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        className="rich-editor-content"
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder || 'Začnite pisati...'}
        suppressContentEditableWarning
      />

      {/* Help text */}
      <div className="rich-editor-help">
        <span>Namig: Uporabite <kbd>Ctrl+B</kbd> za krepko, <kbd>Ctrl+I</kbd> za ležeče</span>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>Vstavi sliko</h3>
              <button 
                type="button"
                className="modal-close-btn"
                onClick={() => setShowImageModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="image-modal-body">
              <div className="modal-form-group">
                <label>URL slike *</label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/slika.jpg"
                  autoFocus
                />
              </div>
              
              <div className="modal-form-group">
                <label>Opis slike (alt)</label>
                <input
                  type="text"
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                  placeholder="Opis slike za dostopnost"
                />
              </div>

              {imageUrl && (
                <div className="image-preview">
                  <p>Predogled:</p>
                  <img 
                    src={imageUrl} 
                    alt={imageAlt || 'Predogled'} 
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
            
            <div className="image-modal-footer">
              <button 
                type="button"
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowImageModal(false)}
              >
                Prekliči
              </button>
              <button 
                type="button"
                className="modal-btn modal-btn-primary"
                onClick={insertImage}
                disabled={!imageUrl}
              >
                Vstavi sliko
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;

